import { AppFormControlService } from 'ng2-app/modules/borrower/services/app-form-control.service';
import { RequirementField, FieldGroup, DisplayableInfo } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { DisplayableField } from 'ng2-app/modules/borrower/components/borrower-details/app-requirements/components/borrower-attribute/borrower-attribute.component';
import { fade } from 'app/util/animations/fade';
import { UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiHttpClient } from 'app/http/clients/api';

@Component({
    selector: 'app-requirement-form',
    templateUrl: 'app-requirement-form.component.html',
    styleUrls: ['app-requirement-form.component.scss'],
    animations: [fade],
    standalone: false
})
export class AppRequirementFormComponent implements OnInit, OnDestroy {

    @Input() fields: RequirementField[];
    @Input() borrowerId: number;
    @Input() editable: boolean;
    @Input() displayableInfo: DisplayableInfo | undefined;

    form: UntypedFormGroup;
    displayableFields: DisplayableField[];
    hasEditableFields: boolean;

    error = '';
    saving = false;
    success = false;
    destroyed$ = new Subject<void>();

    private _saveFields: Array<{
        alias: string;
        value: any;
    }>;

    private addressGroupNames = ['ownerAddress', 'businessAddress'];

    constructor(
        private afcs: AppFormControlService,
        private http: ApiHttpClient
    ) {}

    ngOnInit() {
        this.constructForm();
        this.updateFormValues();

        this.afcs.formUpdates.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.updateFormValues();
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    fieldTrackId(index, field: DisplayableField) {
        return index + field.name;
    }

    /**
     * Determines which fields have values which changed and updates their corresponding
     * controls accordingly.
     */
    updateFormValues() {
        const aliasesWithControls = Object.keys(this.form.controls);
        const currentAliases = this.fields.map(f => f.alias);

        // Update fields
        const toUpdate = aliasesWithControls.filter(a => currentAliases.includes(a));
        toUpdate.forEach((alias, index) => {
            const control = this.form.controls[alias];
            const field = this.fields.find(f => f.alias === alias);
            if (!field) {
                return;
            }
            if (control && control.pristine && control.value !== field.value) {
                control.setValue(field.value);
            }
        });

        // Remove controls for any removed fields
        const toRemove = aliasesWithControls.filter(a => !currentAliases.includes(a));
        toRemove.forEach((alias) => this.form.removeControl(alias));

        // Add controls for any new fields
        const toAdd = currentAliases.filter(a => !aliasesWithControls.includes(a));
        toAdd.forEach((alias) => {
            const field = this.fields.find(f => f.alias === alias);
            if (!field) {
                return;
            }
            this.form.addControl(alias, this.afcs.controlForField(field));
        });

        // Build array of displayable fields
        this.displayableFields = this.fields.filter(f => (!f.group || !this.addressGroupNames.includes(f.group)));

        this.hasEditableFields = this.displayableFields.filter(field => field.editable).length > 0;
    }

    constructForm() {
        this.form = this.afcs.toFormGroup(this.fields);
    }

    onSubmit() {
        this.error = '';

        this._saveFields = this.fields
            .filter((field) => {
                const ctrl = this.form.controls[field.alias];
                return ctrl.dirty
                    && this.form.controls[field.alias].value !== ''
                    && this.form.controls[field.alias].value !== null
                    && this.form.controls[field.alias].value !== undefined
                    && this.form.controls[field.alias].value !== field.value;
            })
            .map((field) => {
                const ctrl = this.form.controls[field.alias];
                return {
                    'alias': field.alias,
                    'value': ctrl.value
                };
            });

        if (this._saveFields.length < 1) {
            this.onError('No changes were found or all values were empty.');
            return;
        }

        this.saving = true;

        this.http.put(`/matching/questions/${this.borrowerId}`, this._saveFields).subscribe({
            next: () => this.success = true,
            error: error => this.onError(error),
            complete: () => {
                this.saving = false;
                setTimeout(() => {
                    this.success = false;
                }, 1500);
            }
          });
    }

    onError(err: any) {
        this.error = err;
    }
}
